import "react-quill/dist/quill.snow.css";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import QuillEditor from "react-quill";

import styles from "./RichEditor.css";

const insertBase64Image = (quillEditor, file) => {
  const reader = new FileReader();

  reader.onload = () => {
    const range = quillEditor.getSelection(true);
    quillEditor.insertEmbed(range.index, "image", reader.result, "user");
    quillEditor.setSelection(range.index + 1);
  };
  reader.readAsDataURL(file);
};

const stripImages = (html) =>
  typeof html === "string" ? html.replace(/<img\b[^>]*>/gi, "") : html;

const isHtml = (str) => typeof str === "string" && /<[a-z][\s\S]*>/i.test(str);

const RichEditor = ({
  editorValue,
  onChange,
  disabled,
  loading = false,
  readOnly = false,
  allowImages = true,
}) => {
  const [value, setValue] = useState("");

  useEffect(() => {
    setValue(allowImages ? editorValue || "" : stripImages(editorValue) || "");
  }, [editorValue, allowImages]);

  const quill = useRef();

  function textAreaChange(text) {
    const sanitized = allowImages ? text : stripImages(text);
    setValue(sanitized);
    if (onChange) onChange(sanitized);
  }

  const imageHandler = useCallback(() => {
    const input = document.createElement("input");
    input.setAttribute("type", "file");
    input.setAttribute("accept", "image/*");
    input.click();
    input.onchange = () => {
      const file = input.files[0];

      if (file) {
        insertBase64Image(quill.current.getEditor(), file);
      }
    };
  }, []);

  useEffect(() => {
    if (!quill.current || disabled || readOnly || !allowImages) return;

    const editor = quill.current.getEditor();
    const editorRoot = editor.root;

    const handlePaste = (event) => {
      const clipboardData = event.clipboardData;

      if (!clipboardData || !clipboardData.items) return;

      for (const item of clipboardData.items) {
        if (item.type.startsWith("image/")) {
          event.preventDefault();
          event.stopPropagation();
          insertBase64Image(editor, item.getAsFile());

          return;
        }
      }
    };

    const handleDrop = (event) => {
      const files = event.dataTransfer?.files;

      if (!files || files.length === 0) return;

      const imageFile = Array.from(files).find((file) =>
        file.type.startsWith("image/")
      );

      if (imageFile) {
        event.preventDefault();
        event.stopPropagation();
        insertBase64Image(editor, imageFile);
      }
    };

    const handleDragOver = (event) => {
      const hasImage = Array.from(event.dataTransfer?.types || []).includes(
        "Files"
      );

      if (hasImage) {
        event.preventDefault();
      }
    };

    editorRoot.addEventListener("paste", handlePaste);
    editorRoot.addEventListener("drop", handleDrop);
    editorRoot.addEventListener("dragover", handleDragOver);

    return () => {
      editorRoot.removeEventListener("paste", handlePaste);
      editorRoot.removeEventListener("drop", handleDrop);
      editorRoot.removeEventListener("dragover", handleDragOver);
    };
  }, [disabled, allowImages, readOnly]);

  const modules = useMemo(
    () => ({
      toolbar: {
        container: [
          [{ header: [1, 2, 3, 4, false] }],
          ["bold", "italic", "underline", "blockquote"],
          [{ color: [] }],
          [
            { list: "ordered" },
            { list: "bullet" },
            { indent: "-1" },
            { indent: "+1" },
          ],
          allowImages ? ["link", "image"] : ["link"],
          ["clean"],
        ],
        handlers: allowImages ? { image: imageHandler } : {},
      },
      clipboard: {
        matchVisual: true,
      },
    }),
    [imageHandler, allowImages]
  );

  const formats = useMemo(() => {
    const base = [
      "header",
      "bold",
      "italic",
      "underline",
      "strike",
      "blockquote",
      "list",
      "bullet",
      "indent",
      "link",
      "color",
      "clean",
      "align",
      "background",
    ];

    return allowImages ? [...base, "image"] : base;
  }, [allowImages]);

  const isPlainText = !!value && !isHtml(value);
  const isViewOnly = disabled || readOnly;

  const plainTextSharedStyle = {
    width: "100%",
    padding: "12px",
    border: "1px solid #ccc",
    borderRadius: "12px",
    fontSize: "16px",
    fontWeight: "400",
    lineHeight: "24px",
    color: "var(--gray-500, #717186)",
    boxSizing: "border-box",
    whiteSpace: "pre-wrap",
    fontFamily: "inherit",
    marginTop: "1rem",
  };

  const plainTextContent = isViewOnly ? (
    <pre
      style={{
        ...plainTextSharedStyle,
        overflowWrap: "break-word",
      }}
    >
      {value}
    </pre>
  ) : (
    <textarea
      value={value}
      onChange={(e) => textAreaChange(e.target.value)}
      style={{
        ...plainTextSharedStyle,
        display: "block",
        height: "300px",
        minHeight: "300px",
        resize: "vertical",
      }}
    />
  );

  return (
    <div className={styles.wrapper}>
      <div style={{ position: "relative" }}>
        {isPlainText ? (
          plainTextContent
        ) : (
          <QuillEditor
            ref={(el) => (quill.current = el)}
            className={`${styles.editor} ${
              disabled || readOnly ? "ql-disabled" : ""
            }`}
            theme="snow"
            value={value}
            formats={formats}
            readOnly={disabled || readOnly}
            // `modules` is one of react-quill's "dirtyProps" — changing it
            // (even just its shape/identity) forces a full teardown and
            // re-instantiation of the underlying Quill editor, which is what
            // caused the editor to visibly disappear when toggling
            // readOnly/disabled. Keep it stable and hide the toolbar with
            // CSS (.ql-disabled .ql-toolbar) instead.
            modules={modules}
            onChange={(newValue) =>
              !disabled && !readOnly && textAreaChange(newValue)
            }
          />
        )}
        {loading && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(248, 249, 250, 0.9)",
              cursor: "not-allowed",
              zIndex: 10,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "14px",
              color: "#495057",
              fontWeight: "500",
            }}
          >
            <div
              style={{
                backgroundColor: "rgba(255, 255, 255, 0.9)",
                padding: "12px",
                borderRadius: "8px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <div
                className="loader-spinner"
                style={{
                  width: "14px",
                  height: "14px",
                  border: "2px solid #f3f3f3",
                  borderTop: "2px solid var(--green-900, #006546)",
                  borderRadius: "50%",
                  animation: "spin 1s linear infinite",
                }}
              ></div>
              Processing audio...
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export { RichEditor };
